"use client";

import { ScrollContainer } from "@/components/platform/scroll-container";
import { cn } from "@/lib/utils";
import { useEventConnect } from "@/store/eventConnect";
import { getGuessDisplayInfo } from "@/utils/guess";
import { convertCentPriceToChancePercent, formatCentPriceAsChancePercent } from "@/utils/price";
import { useEffect, useRef } from "react";
import { GuessItem } from "./sport-event-components";

interface PredictionCardContainerProps {
    sliderValues: GuessItem[];
    selectedIndex: number;
    onSliderChange?: ( index: number ) => void;
}

export default function PredictionCardContainer(
    { sliderValues, selectedIndex, onSliderChange }: PredictionCardContainerProps,
) {
    const scrollContainerRef = useRef<HTMLDivElement>( null );

    // Initial scroll or sync scroll
    useEffect( () => {
        if ( scrollContainerRef.current ) {
            // ScrollContainer renders a relative wrapper div first, then the actual scroll container is the first child (after gradients)
            // It uses forwardRef but attaches it to the inner scroll container.
            const container = scrollContainerRef.current;
            const element = container.children[selectedIndex] as HTMLElement;
            if ( element ) {
                // Manually calculate the scroll position to keep it in the center without triggering parent scroll
                const containerWidth = container.clientWidth;
                const elementOffsetLeft = element.offsetLeft;
                const elementWidth = element.clientWidth;

                // calculation: element.left + (element.width / 2) - (container.width / 2)
                const scrollLeft = elementOffsetLeft + ( elementWidth / 2 ) - ( containerWidth / 2 );

                container.scrollTo( { left: scrollLeft, behavior: "smooth" } );
            }
        }
    }, [ selectedIndex ] );

    if ( !sliderValues || sliderValues.length <= 1 ) return null;

    return (
        <ScrollContainer
            ref={scrollContainerRef}
            className="snap-x scroll-smooth select-none items-center mb-3"
            gap="gap-3"
            gradientWidth="w-[0]"
        >
            {sliderValues.map( ( data, index ) => {
                const isSelected = index === selectedIndex;
                return (
                    <PredictionCard
                        key={index}
                        data={data}
                        isSelected={isSelected}
                        onClick={() => onSliderChange?.( index )}
                        boldText={data.title || ""}
                    />
                );
            } )}
        </ScrollContainer>
    );
}

interface PredictionCardProps {
    data: GuessItem;
    isSelected: boolean;
    onClick: () => void;
    boldText: string;
    normalText?: string;
}

function PredictionCard( { data, isSelected, onClick, boldText, normalText }: PredictionCardProps ) {
    const marketPrice = useEventConnect( ( state ) => state.guesses[data.guessNo] );
    const yesRealtimePrice = marketPrice?.yesPrice ?? data.yesRealtimePrice ?? 0;
    const { title, subTitle } = getGuessDisplayInfo( {
        guessGroupNo: data.guessGroupNo,
        groupTitle: data.groupTitle,
        guessCategoryId: data.guessCategoryId,
        guessTitle: data.title,
    } );
    const yesPercent = convertCentPriceToChancePercent( yesRealtimePrice );
    const yesPercentText = formatCentPriceAsChancePercent( yesRealtimePrice );

    return (
        <div
            className={cn(
                "flex flex-col shrink-0 min-w-[70px] max-w-[120px] px-2 py-1.5 rounded-lg cursor-pointer snap-center relative transition-colors duration-200 border leading-none",
                isSelected ? "border-action-primary bg-no-subtle" : "border-stroke",
            )}
            onClick={( e ) => {
                e.stopPropagation();
                onClick();
            }}
        >
            {/* Title container */}
            <div className="flex flex-col w-full">
                <div className="text-xs leading-none whitespace-nowrap overflow-hidden text-ellipsis w-full">
                    <span className="font-semibold text-t1">{title}</span>
                    {subTitle && <span className={cn( "font-normal ml-0.5 text-t1" )}>{subTitle}</span>}
                </div>
            </div>

            {/* Subtitle */}
            <div className="flex flex-col justify-center mb-1 w-full">
                <div className="whitespace-nowrap overflow-hidden text-ellipsis w-full">
                    <span className={cn( "font-medium text-[10px] text-t1" )}>Yes</span>
                    <span className={cn( "font-medium text-[10px] ml-0.5 text-t1" )}>{yesPercentText}</span>
                </div>
            </div>

            {/* Progress bar */}
            <div className="relative w-full h-[3px] flex shrink-0 rounded-[100px] overflow-hidden">
                <div className={cn( "absolute inset-0 rounded-[100px] bg-no" )} />
                <div
                    className={cn( "absolute left-0 top-0 bottom-0 rounded-[100px] bg-yes" )}
                    style={{ width: `${yesPercent}%` }}
                />
            </div>
        </div>
    );
}
